
import { PartialObserver, Subject, Subscriber, Subscription, from, of as observableOf } from 'rxjs'

import Backdrop from '@mui/material/Backdrop'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { EditFileFolderDialog } from 'components/EditFileFolderDialog'
import FileUploadSnackbar from 'components/FileUploadSnackbar'
import { FileFolder, MyFile } from 'interfaces'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { catchError, mergeMap, takeUntil } from 'rxjs/operators'
import { HTTP_STATUS_PAYMENT_REQUIRED } from 'services/net'
import { checkProductLimit, getProductUpgradeByLimitKey } from 'services/product'
import { userUnlimitedLimitsSelector } from 'services/product/selectors'
import { message } from 'services/snackbar'
import { SnackType } from 'services/snackbar/interfaces/PendingTreat'
import {
  copyFileToFolder,
  createFolder,
  getStorageInfo,
  getUploads,
  setSelectedFile,
  setUploaderFiles,
  setUploadsDialogOpen,
  uploadFile
} from 'services/uploads/actions'
import {
  editFolderSelector,
  myFileFoldersArraySelector,
  myUploadsStorageSelector,
  selectedFileSelector,
  uploadDialogFilesSelector,
  uploadDiaogOpenSelector
} from 'services/uploads/selectors'
import { FileUploadInfo, StorageInfo } from 'services/uploads/state'
import { userProductSelector } from 'services/users/selectors'
import { debounce, PPProduct } from 'shared'
import { LIMIT_MY_LIBRARY_STORAGE, MAX_PRODUCT_ORDER } from 'shared/constants'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { DEFAULT_STUDIO_EXPORT_FILE_NAME } from 'utils/file'
import MultiUploadManager from './MultiUploadManager'
import styles from './MultiUploadManager.pcss'
const MAX_CONCURRENT_REQUESTS = 5
const PERCENT_MULTIPLIER = 100

interface ConnectedMultiUploadManagerContainerProps {
  folders: FileFolder[]
  files: { [name: string]: File }
  dialogOpen: boolean
  fileSizeLimits: { [key: string]: number }
  storage: StorageInfo
  product?: PPProduct
  isStorageUnlimited: boolean
  copyMyFile?: MyFile
  editFolder?: FileFolder
  fetchUploads: () => Promise<any>
  createFolder: (name: string, color: string, isPrivate: boolean, featuredImage?: File, coverImages?: File[]) => Promise<any>
  uploadFile: (
    file: File,
    folder: Partial<FileFolder>,
    width: number,
    height: number,
    rotate?: number,
    progressSubscriber?: PartialObserver<ProgressEvent>,
    uploadId?: string,
    compress?: boolean
  ) => Promise<any>
  showNotification: (text: string, type?: SnackType, timeout?: number) => void
  onCloseDialog: () => void
  setUploaderFiles: (files: { [name: string]: File }) => void
  checkLimit: (limitKey: string, limitValue: number) => boolean
  clearCopyFile: () => void
  copyFile: (file: MyFile, folder: { id: string, name: string }) => Promise<any>
  getProductUpgrade: (value: number) => PPProduct | undefined
  refreshStorageInfo: () => void
}

type MultiUploadManagerContainerProps = ConnectedMultiUploadManagerContainerProps

interface MultiUploadsState {
  [key: string]: {
    file: File
    folder: Partial<FileFolder>
    width: number
    height: number
    progress: number
    error: boolean
    rotate?: number
  }
}

function MultiUploadManagerContainer(props: MultiUploadManagerContainerProps) {
  const intl = useIntl()
  const [uploads, setUploads] = useState<MultiUploadsState>({})

  const {
    storage,
    product,
    isStorageUnlimited,
    folders,
    files,
    dialogOpen,
    fileSizeLimits,
    copyMyFile,
    editFolder,
    fetchUploads,
    createFolder,
    uploadFile: uploadFileAction,
    showNotification,
    onCloseDialog,
    setUploaderFiles,
    checkLimit,
    clearCopyFile,
    copyFile: copyFileAction,
    getProductUpgrade,
    refreshStorageInfo
  } = props

  const fileUpload$ = useRef<Subject<any>>(null)
  const copyFileToNewFolder$ = useRef<Subscription | null>(null)
  const unmount$ = useRef<Subject<boolean>>(null)

  const updateStorage = useRef(
    debounce(() => {
      refreshStorageInfo()
    // eslint-disable-next-line no-magic-numbers
    }, 2000)
  ).current

  const onProgress = useCallback((fileName: string) => (e: ProgressEvent) => {
    const value = (e.loaded / e.total) * PERCENT_MULTIPLIER

    setUploads(prevState => ({
      ...prevState,
      [fileName]: {
        ...prevState[fileName],
        progress: value
      }
    }))
  }, [])

  const onFileUploadError = useCallback((id: string) => {
    setUploads(prevState => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        error: true
      }
    }))
  }, [])

  // Monotonic counter so two files with the same name enqueued in the same
  // millisecond still get distinct upload ids.
  const uploadCounter = useRef(0)

  // Latest handler bundle for the long-lived upload subscription. Read via the
  // ref so the mount effect below stays mount-only and never tears down active
  // uploads when a handler identity (e.g. intl on locale change) changes.
  const handlersRef = useRef({ uploadFileAction, showNotification, intl, onProgress, onFileUploadError })
  handlersRef.current = { uploadFileAction, showNotification, intl, onProgress, onFileUploadError }

  const checkSpaceForFile = useCallback((fileSize: number): boolean => {
    const usedBytesNext = storage.used + fileSize
    const noAvailableUpgrades = product?.order === MAX_PRODUCT_ORDER
    const hasSpace = checkLimit(LIMIT_MY_LIBRARY_STORAGE, usedBytesNext)

    // checkLimit already messages when the plan advertises unlimited storage
    if (!hasSpace && noAvailableUpgrades && !isStorageUnlimited) {
      showNotification(
        intl.formatMessage({ id: 'uploads.notifications.max-limit-reached' }),
        'warning',
        NOTIFICATION_DURATION_LONG
      )
    }
    return hasSpace
  }, [storage.used, product?.order, isStorageUnlimited, checkLimit, showNotification, intl])

  const uploadFiles = useCallback((files: Array<FileUploadInfo>, folders: FileFolder[]) => {
    const filesSize = files.reduce((size, fileData) => {
      size += fileData.file.size
      return size
    }, 0)

    const usedBytesNext = storage.used + filesSize
    const canUpload = checkLimit(LIMIT_MY_LIBRARY_STORAGE, usedBytesNext)
    const hasUpgrade = getProductUpgrade(usedBytesNext) !== undefined

    if (!canUpload) {
      // checkLimit already messages when the plan advertises unlimited storage
      if (!hasUpgrade && !isStorageUnlimited) {
        showNotification(
          intl.formatMessage({ id: 'uploads.notifications.max-limit-reached' }),
          'warning',
          NOTIFICATION_DURATION_LONG
        )
      }
      return
    }

    showNotification(intl.formatMessage({ id: 'uploads.notifications.multi-upload-started' }), 'info')

    folders.forEach((folder: FileFolder) => {
      files.forEach(upload => {
        const id = `${upload.file.name}-${Date.now()}-${uploadCounter.current++}`
        fileUpload$.current?.next({
          id,
          file: upload.file,
          folder: { id: folder.id, name: folder.name },
          width: upload.width,
          height: upload.height,
          rotateBy: upload.rotateBy
        })

        setUploads(prevState => ({
          ...prevState,
          [id]: {
            file: upload.file,
            folder: { id: folder.id, name: folder.name },
            width: upload.width,
            height: upload.height,
            progress: 0,
            error: false,
            rotateBy: upload.rotateBy
          }
        }))
      })
    })
  }, [storage.used, isStorageUnlimited, checkLimit, getProductUpgrade, showNotification, intl])

  const onCloseUploadSnackbar = useCallback((id: string) => {
    setUploads(prevState => {
      const newUploads = { ...prevState }
      delete newUploads[id]

      if (Object.keys(newUploads).length === 0) {
        updateStorage()
      }

      return newUploads
    })
  }, [updateStorage])

  const copyFile = useCallback((file: MyFile, folder: { id: string, name: string }) => {
    const canUpload = checkSpaceForFile(file.size)
    if (!canUpload) {
      return
    }

    copyFileAction(file, folder)
      .then(() => {
        showNotification(intl.formatMessage({ id: 'uploads.notifications.file-copied' }), 'success')
      })
      .catch(() => {
        showNotification(intl.formatMessage({ id: 'uploads.notifications.file-copy-failed' }), 'error')
      })

    showNotification(intl.formatMessage({ id: 'uploads.notifications.multi-upload-started' }))
  }, [checkSpaceForFile, copyFileAction, showNotification, intl])

  const copyFileToNewFolder = useCallback((file: MyFile, folder: { name: string, color: string, isPrivate: boolean }) => {
    showNotification(intl.formatMessage({ id: 'uploads.notifications.multi-upload-started' }))

    copyFileToNewFolder$.current = from(createFolder(folder.name, folder.color, folder.isPrivate))
      .pipe(
        takeUntil(unmount$.current!),
        mergeMap((response) => {
          return copyFileAction(file, { id: response.id, name: folder.name })
        })
      )
      .subscribe(() => {
        showNotification(intl.formatMessage({ id: 'uploads.notifications.file-copied' }), 'success')
      }, () => {
        showNotification(intl.formatMessage({ id: 'uploads.notifications.file-copy-failed' }), 'error')
      })
  }, [createFolder, copyFileAction, showNotification, intl])

  useEffect(() => {
    fetchUploads()
    fileUpload$.current = new Subject()
    fileUpload$.current.pipe(mergeMap(
      ({ id, file, folder, width, height, rotateBy }: any) => {
        const progress$ = { next: handlersRef.current.onProgress(id) }
        const shouldCompress = file.name === DEFAULT_STUDIO_EXPORT_FILE_NAME
        return from(handlersRef.current.uploadFileAction(file, folder, width, height, rotateBy, progress$, id, shouldCompress))
          .pipe(catchError((error) => {
            return observableOf({ error })
          }))
      },
      MAX_CONCURRENT_REQUESTS
    ))
      .subscribe((res: any) => {
        const { showNotification, intl, onFileUploadError } = handlersRef.current

        if (res.error) {
          if (res.error.source?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
            showNotification(intl.formatMessage({ id: 'uploads.notifications.error-storage-full' }), 'error')
          } else {
            showNotification(intl.formatMessage({ id: 'uploads.error.upload-failed' }), 'error')
          }
          onFileUploadError(res.error.uploadId)
          return
        }

        setUploads(prevState => ({
          ...prevState,
          [res.uploadId]: {
            ...prevState[res.uploadId],
            progress: PERCENT_MULTIPLIER
          }
        }))
      })

    unmount$.current = new Subject()

    return () => {
      fileUpload$.current?.unsubscribe()
      unmount$.current?.next(true)

      if (copyFileToNewFolder$.current) {
        copyFileToNewFolder$.current.unsubscribe()
      }
    }
  }, [fetchUploads])

  const isUploading = Object.keys(uploads).length !== 0

  return (
    <Fragment>
      {editFolder ? (
        <EditFileFolderDialog
          folder={editFolder}
          fileUploads={files}
          setFileUploads={setUploaderFiles}
          onUploadFiles={uploadFiles}
        />
      ) : (
        <MultiUploadManager
          folders={folders}
          files={files}
          dialogOpen={dialogOpen}
          fileSizeLimits={fileSizeLimits}
          copyFile={copyMyFile}
          uploadFiles={uploadFiles}
          setFiles={setUploaderFiles}
          createFolder={createFolder}
          onCloseDialog={onCloseDialog}
          showNotification={showNotification}
          clearCopyFile={clearCopyFile}
          onCopyFile={copyFile}
          onCopyFileToNewFolder={copyFileToNewFolder}
        />
      )}
      <Backdrop open={isUploading} invisible classes={{ root: styles.backdrop }}>
        <div className={styles['backdrop-panel']}>
          {
            Object.keys(uploads).map(id => (
              <FileUploadSnackbar
                id={id}
                key={id}
                file={uploads[id].file}
                progress={uploads[id].progress}
                error={uploads[id].error}
                onClose={onCloseUploadSnackbar}
              />
            ))
          }
        </div>
      </Backdrop>
    </Fragment>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    folders: myFileFoldersArraySelector(state),
    files: uploadDialogFilesSelector(state),
    dialogOpen: uploadDiaogOpenSelector(state),
    fileSizeLimits: myUploadsStorageSelector(state).fileSizeLimits,
    storage: myUploadsStorageSelector(state),
    product: userProductSelector(state),
    isStorageUnlimited: Boolean(userUnlimitedLimitsSelector(state)[LIMIT_MY_LIBRARY_STORAGE]),
    copyMyFile: selectedFileSelector(state),
    editFolder: editFolderSelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    fetchUploads: () => dispatch(getUploads()).unwrap(),
    createFolder: (name: string, color: string, isPrivate: boolean, featuredImage?: File, coverImages?: File[]) => {
      return dispatch(createFolder({ name, color, isPrivate, featuredImage, coverImages })).unwrap()
    },
    uploadFile: (
      file: File,
      folder: Partial<FileFolder>,
      width: number,
      height: number,
      rotateByDegrees?: number,
      progressSubscriber?: Subscriber<any>,
      uploadId?: string,
      compress?: boolean
    ) => {
      return dispatch(uploadFile({ file, folder, width, height, rotateByDegrees, progressSubscriber, uploadId, compress }))
        .unwrap()
    },
    showNotification: (text: string, type?: SnackType, timeout?: number) => dispatch(message(text, type, timeout)),
    onCloseDialog: () => {
      dispatch(setUploadsDialogOpen(false))
      dispatch(setSelectedFile(undefined))
    },
    copyFile: (file: MyFile, folder: { id: string, name: string }) => dispatch(copyFileToFolder({ file, folder }))
      .unwrap()
      .then(() => dispatch(getStorageInfo())),
    refreshStorageInfo: () => dispatch(getStorageInfo()),
    setUploaderFiles: (files: { [name: string]: File }) => dispatch(setUploaderFiles(files)),
    checkLimit: (limitKey: string, limitValue: number) => dispatch(checkProductLimit(limitKey, limitValue)),
    clearCopyFile: () => dispatch(setSelectedFile(undefined)),
    getProductUpgrade: (value: number) => dispatch(getProductUpgradeByLimitKey(LIMIT_MY_LIBRARY_STORAGE, value))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MultiUploadManagerContainer)
